<template>
  <div :class="$style.previewWrapper">
    <div v-if="isOverlay" :class="$style.overlayBackground" @click="onClose?.()"></div>
    <div v-if="isOverlay" :class="$style.closeButtonContainer">
      <SCAIconButton
        :class="$style.closeButton"
        appearance="inverted"
        type="ghost"
        size="xsmall"
        icon="phosphor/x"
        @click="onClose?.()"
      />
    </div>
    <div :class="[$style.previewContainer, isOverlay ? $style.overlayMode : '']" @click.stop>
      <ImagePreview v-if="isImage(fileType) && fileUrl" :file-url="fileUrl" :file-name="fileName" :on-close="onClose" />
      <PdfPreview v-else-if="isPdf(fileType) && fileUrl" :file-url="fileUrl" :on-close="onClose" />
      <EmptyState
        v-else
        :message="fileUrl ? $t('common.label.unsupportedFileType') : $t('common.label.filesPreview')"
        :class="$style.empty"
      />
    </div>
  </div>
</template>

<script lang="ts" setup>
import { SCAIconButton } from "@scayla/components";
import { onMounted, onUnmounted } from "vue";
import { useI18n } from "vue-i18n";

import { isImage, isPdf } from "@/utils/fileAttachments";

import EmptyState from "./FilePreviewEmptyState.vue";
import ImagePreview from "./ImagePreview.vue";
import PdfPreview from "./PdfPreview.vue";

const { t: $t } = useI18n();

const props = withDefaults(
  defineProps<{
    fileUrl?: string;
    fileType?: string;
    fileName?: string;
    isOverlay: boolean;
    /** Closes the preview; without it there is no close button, as on the standalone preview page. */
    onClose?: () => void;
  }>(),
  {
    fileUrl: undefined,
    fileType: undefined,
    fileName: undefined,
    onClose: undefined,
  },
);

const handleEscKey = (event: KeyboardEvent) => {
  if (props.isOverlay && event.key === "Escape") {
    props.onClose?.();
  }
};

onMounted(() => {
  window.addEventListener("keydown", handleEscKey);
});

onUnmounted(() => {
  window.removeEventListener("keydown", handleEscKey);
});
</script>

<style lang="scss" module>
.previewWrapper {
  all: unset;
  display: contents;
}
.previewContainer {
  position: relative;
  z-index: 1;
  overflow: auto;
  height: 100%;
}

.overlayBackground {
  position: fixed;
  z-index: 1000;
  background-color: rgba(0, 0, 0, 0.5);
  top: 0;
  bottom: 0;
  left: 0;
  right: 0;
}

.overlayMode {
  position: fixed;
  z-index: 1001;
  top: 0;
  left: 5vw;
  right: 5vw;
  bottom: 0;
  overflow: hidden;
  height: 100%;
  display: flex;
  flex-direction: column;

  @media (max-width: 1024px) {
    left: 6vw;
    right: 6vw;
  }

  @media (max-width: 426px) {
    top: 4vh;
    left: 4vw;
    right: 4vw;
    bottom: 4vh;
    height: 94%;
  }
}

.closeButton {
  aspect-ratio: 1/1;

  svg {
    width: var(--size-xSmall);
    height: var(--size-xSmall);
  }
}

.closeButtonContainer {
  position: fixed;
  z-index: 1002;
  left: 5vw;
  top: var(--dimension-200);
  transform: translateX(calc(-100% - 22px));
  display: flex;
  align-items: center;
  justify-content: center;

  @media (max-width: 1024px) {
    transform: translateX(calc(-96%));
  }

  @media (max-width: 478px) {
    display: none;
  }
}

.empty {
  background: white;
  text-align: center;
  padding: 0 var(--space-component-padding-small);
}
</style>
